import React, { useContext, useEffect, useState } from 'react';
import { Button, Checkbox, FormControlLabel, InputAdornment } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { warehouseApi as api } from 'api/warehouse';
import { UserContext } from 'contexts/UserContext';
import { DataTable, FormComponent, Notifications } from 'shared';
import { csvUtil } from 'shared/utils/csvUtil';

import { ViewLocationsInZone } from './viewLocationsInZone';

function BulkCreateLocations({ v2Locations }) {
    const [notification, setNotification] = useState({ text: null, level: null });
    const [tempZones, setTempZones] = useState([]);
    const [types, setTypes] = useState([]);
    const [creatingType, setCreatingType] = useState({});
    const [reservePrefix, setReservePrefix] = useState('');
    const [columns, setColumns] = useState([]);

    const [toolbarActions, setToolbarActions] = useState([]); //
    const [createResults, setCreateResults] = useState(null);
    const [createLocProgress, setCreateLocProgress] = useState({});
    const [zone, setZone] = useState('');
    const [viewNewAndExistingLocationsInZone, setViewNewAndExistingLocationsInZone] = useState(false);
    const [replenishmentCreationMethod, setReplenishmentCreationMethod] = useState('');
    const [childLineNumbersError, setChildLineNumbersError] = useState(null);

    const [locationsCreatedResults, setLocationsCreatedResults] = useState(null);

    const user = useContext(UserContext);
    const startsAtEndsAtTypes = ['QC', 'CART', 'TRANSFER', 'FORKLIFT', 'DROP ZONE'];
    const zoneAisleBayTypes = ['RESERVE'];
    const replenTypes = ['REPLENISHMENT'];
    const singleCreationTypes = ['CONTAINER', 'TRAILER', 'STAGING', 'PREP'];

    const { roles } = useContext(UserContext);
    const canOffsite = roles?.some((role) => role.name === 'OFFSITE_TRANSFERS');

    const displayFormField = (accessor, formData) => {
        if (!formData.type?.value) {
            return false;
        }
        if (!creatingType.type) {
            return false;
        }
        if (Object.keys(creatingType.staticValues).includes(accessor)) {
            return false;
        }

        /**
         * Max Pallets should only be displayed if inventory type is Pallet
         */
        if (accessor === 'maxPallets' && formData.inventoryType === 'Case') {
            return false;
        }

        if (accessor === 'multipleSku' && formData.multipleLot === 'NO') {
            return false;
        }

        if (accessor === 'tempZone' && zoneAisleBayTypes.includes(formData.type?.value)) {
            return false;
        }

        return true;
    };

    const setResultsColumnsByType = (type) => {
        if (startsAtEndsAtTypes.includes(type)) {
            setColumns([...resultsColumns, ...numberRangeColumns, { Header: 'Errors', accessor: 'errors' }]);
        } else if (zoneAisleBayTypes.includes(type)) {
            setColumns([...resultsColumns, ...zoneAisleBayColumns, { Header: 'Errors', accessor: 'errors' }]);
        } else if (replenTypes.includes(type)) {
            setColumns([...resultsColumns, { Header: 'Errors', accessor: 'errors' }]);
        }
    };

    let resultsColumns = [
        { Header: 'Name', accessor: 'name' },
        { Header: 'Type', accessor: 'type' },
        { Header: 'Temp Zone', accessor: 'tempZone' },
        { Header: 'OG/CV', accessor: 'productionMethod' },
        { Header: 'Sellable', accessor: 'usable', type: 'checkbox' },
        { Header: 'Inventory Type', accessor: 'inventoryType' },
        { Header: 'Inventory Source', accessor: 'inventorySource' },
        { Header: 'Max Pallets', accessor: 'maxPallets' },
        { Header: 'Multiple Lot', accessor: 'multipleLot', type: 'checkbox' },
        { Header: 'Multiple Sku', accessor: 'multipleSku', type: 'checkbox' },
    ];

    let zoneAisleBayColumns = [
        { Header: 'Zone', accessor: 'zone' },
        { Header: 'Aisle', accessor: 'aisle' },
        { Header: 'Bay', accessor: 'bay' },
        { Header: 'Shelf', accessor: 'shelf' },
        { Header: 'Position', accessor: 'position' },
        { Header: 'Errors', accessor: 'errors' },
    ];

    let numberRangeColumns = [{ Header: 'Errors', accessor: 'errors' }];

    const singleCreationFormFields = [
        {
            name: 'suffix',
            label: 'Name',
            gridValue: 12,
            inputElement: 'textField',
            InputProps: {
                startAdornment: (
                    <InputAdornment position="start">
                        <span>{creatingType.prefix}</span>
                    </InputAdornment>
                ),
            },
            inputProps: { required: true, type: creatingType.type === 'CONTAINER' ? 'number' : 'text', integer: true },
            display: singleCreationTypes.includes(creatingType.type),
        },
    ];

    const numberRangeFormFields = [
        {
            name: 'startAt',
            label: 'Start At',
            gridValue: 6,
            inputElement: 'textField',
            InputProps: {
                startAdornment: (
                    <InputAdornment position="start">
                        <span>{creatingType.prefix}</span>
                    </InputAdornment>
                ),
            },
            inputProps: { required: true, type: 'number', integer: true },
            display: (formData) => startsAtEndsAtTypes.includes(formData.type?.value),
        },
        {
            name: 'endAt',
            label: 'End At',
            gridValue: 6,
            inputElement: 'textField',
            InputProps: {
                startAdornment: (
                    <InputAdornment position="start">
                        <span>{creatingType.prefix}</span>
                    </InputAdornment>
                ),
            },
            inputProps: { type: 'number', integer: true },
            display: (formData) => startsAtEndsAtTypes.includes(formData.type?.value),
        },
    ];

    const zoneAisleBayShelfPositionOrReplenFormFields = [
        {
            name: 'tempZoneReserve',
            inputElement: 'select',
            label: 'Temp Zone',
            gridValue: 12,
            display: (formData) => zoneAisleBayTypes.includes(formData.type?.value),
            required: true,
            inputProps: {
                required: true,
                // opts: tempZones,
                opts: tempZones.filter((opt) => opt !== 'All'),
                onChange: (e) => {
                    const val = e.target.value;
                    if (val === 'Ambient (>55)') {
                        setReservePrefix('AB');
                    } else if (val === 'Frozen (0-31)') {
                        setReservePrefix('FZ');
                    } else if (val === 'Refrigerated (36-40)') {
                        setReservePrefix('RF');
                    } else if (val === 'Nightshade (50-55)') {
                        setReservePrefix('NS');
                    } else {
                        setReservePrefix('');
                    }
                },
            },
        },
        {
            name: 'zone',
            label: 'Zone (Integer)',
            gridValue: 8,
            inputElement: 'textField',
            inputProps: { required: true, type: 'number', integer: true, max: '100' },
            display: (formData) => zoneAisleBayTypes.includes(formData.type?.value),
        },
        {
            name: 'viewAisles',
            ignoreOnSubmit: true,
            gridValue: 4,
            display: (formData) => !!formData.zone && zoneAisleBayTypes.includes(formData.type?.value),
            inputElement: () => (
                <FormControlLabel
                    control={
                        <Checkbox
                            onChange={(event) => {
                                setViewNewAndExistingLocationsInZone(event.target.checked);
                            }}
                            color="primary"
                            checked={viewNewAndExistingLocationsInZone}
                        />
                    }
                    label="View Locations In Zone"
                />
            ),
        },
        {
            name: 'aislesHeader',
            ignoreOnSubmit: true,
            inputElement: () => (
                <h5 style={{ textAlign: 'left', padding: 0, margin: 0, fontWeight: 'bold' }}>Aisles (A-Z)</h5>
            ),
            display: (formData) => zoneAisleBayTypes.includes(formData.type?.value),
        },
        {
            name: 'aisleStart',
            label: 'Start at Aisle',
            gridValue: 4,
            inputElement: 'textField',
            inputProps: { required: true },
            display: (formData) => zoneAisleBayTypes.includes(formData.type?.value),
        },
        {
            name: 'aisleEnd',
            label: 'End at Aisle',
            gridValue: 4,
            inputElement: 'textField',
            display: (formData) => zoneAisleBayTypes.includes(formData.type?.value),
        },
        {
            name: 'aislesHint',
            ignoreOnSubmit: true,
            gridValue: 4,
            display: () => createLocProgress.hasOwnProperty('aisles') && !createLocProgress?.errors?.length,
            inputElement: () => (
                <div style={{ fontSize: '13px' }}>
                    <div>
                        {createLocProgress.aisles.range.length} aisles in range:{' '}
                        {createLocProgress.aisles.range.join(', ')}
                    </div>
                    {createLocProgress.aisles.new.length > 0 && (
                        <div>{createLocProgress.aisles.new.length} New Aisles</div>
                    )}
                    {createLocProgress.aisles.existing.length > 0 && (
                        <div>{createLocProgress.aisles.existing.length} Existing Aisles</div>
                    )}
                </div>
            ),
        },
        {
            name: 'replenishmentCreationMethod',
            inputElement: 'select',
            label: 'Creation Method',
            required: true,
            gridValue: 12,
            display: (formData) => replenTypes.includes(formData.type?.value),
            inputProps: {
                opts: [
                    { text: 'Define Ranges', value: 'ranges' },
                    { text: 'Upload CSV', value: 'csv' },
                ],
                required: true,
                onChange: (e) => {
                    setReplenishmentCreationMethod(e.target.value);
                },
                value: replenishmentCreationMethod,
            },
        },
        {
            name: 'replenishmentHint',
            ignoreOnSubmit: true,
            gridValue: 12,
            display: (formData) => replenTypes.includes(formData.type?.value) && replenishmentCreationMethod !== '',
            inputElement: () => {
                if (replenishmentCreationMethod === 'ranges') {
                    return (
                        <div style={{ fontSize: '13px', fontStyle: 'italic', margin: '10px 0' }}>
                            Define ranges for lines, bays, shelves, and lights to generate location names.
                        </div>
                    );
                }
                if (replenishmentCreationMethod === 'csv') {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '10px 0' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    csvUtil.downloadCsv(
                                        'Name,Child Line Numbers\n',
                                        'replenishment-locations-template.csv',
                                    );
                                }}
                            >
                                Download Template
                            </Button>
                            <div style={{ fontSize: '13px', fontStyle: 'italic', textAlign: 'left' }}>
                                <div>Name must begin with "RPLN-".</div>
                                <div>
                                    Child Line Numbers should be a comma-separated list (e.g., 1, 2, 5). These will be
                                    appended to the name to create child locations.
                                </div>
                            </div>
                        </div>
                    );
                }
                return null;
            },
        },
        {
            name: 'replenishmentLocationFile',
            inputElement: 'file',
            label: 'Upload Locations',
            gridValue: 12,
            display: (formData) => replenTypes.includes(formData.type?.value) && replenishmentCreationMethod === 'csv',
            inputProps: {
                required: true,
            },
        },
        {
            name: 'linesHeader',
            ignoreOnSubmit: true,
            inputElement: () => (
                <h5 style={{ textAlign: 'left', padding: 0, marginTop: '20px', fontWeight: 'bold' }}>
                    Replenishment Zones (Integers)
                </h5>
            ),
            display: (formData) =>
                replenTypes.includes(formData.type?.value) && replenishmentCreationMethod === 'ranges',
        },
        {
            name: 'lineStart',
            label: 'Start at Replenishment Zone',
            gridValue: 4,
            inputElement: 'textField',
            inputProps: { required: true, type: 'number', integer: true },
            display: (formData) =>
                replenTypes.includes(formData.type?.value) && replenishmentCreationMethod === 'ranges',
        },
        {
            name: 'lineEnd',
            label: 'End at Replenishment Zone',
            gridValue: 4,
            inputElement: 'textField',
            inputProps: { type: 'number', integer: true },
            display: (formData) =>
                replenTypes.includes(formData.type?.value) && replenishmentCreationMethod === 'ranges',
        },
        {
            name: 'bayHeader',
            ignoreOnSubmit: true,
            inputElement: () => (
                <h5 style={{ textAlign: 'left', padding: 0, marginTop: '20px', fontWeight: 'bold' }}>
                    Bays (Integers)
                </h5>
            ),
            display: (formData) =>
                zoneAisleBayTypes.includes(formData.type?.value) ||
                (replenTypes.includes(formData.type?.value) && replenishmentCreationMethod === 'ranges'),
        },
        {
            name: 'bayStart',
            label: 'Start at Bay',
            gridValue: 4,
            inputElement: 'textField',
            inputProps: { required: true, type: 'number', integer: true },
            display: (formData) =>
                zoneAisleBayTypes.includes(formData.type?.value) ||
                (replenTypes.includes(formData.type?.value) && replenishmentCreationMethod === 'ranges'),
        },
        {
            name: 'bayEnd',
            label: 'End at Bay',
            gridValue: 4,
            inputElement: 'textField',
            inputProps: { type: 'number', integer: true },
            display: (formData) =>
                zoneAisleBayTypes.includes(formData.type?.value) ||
                (replenTypes.includes(formData.type?.value) && replenishmentCreationMethod === 'ranges'),
        },
        {
            name: 'baysHint',
            ignoreOnSubmit: true,
            gridValue: 4,
            display: () => createLocProgress.hasOwnProperty('bays') && !createLocProgress?.errors?.length,
            inputElement: () => (
                <div style={{ fontSize: '13px' }}>
                    <div>
                        {createLocProgress.bays.range.length} bays in range {createLocProgress.bays.range.join(', ')}
                    </div>
                    {createLocProgress.bays.new.length > 0 && <div>{createLocProgress.bays.new.length} New Bays</div>}
                    {createLocProgress.bays.existing.length > 0 && (
                        <div>{createLocProgress.bays.existing.length} Existing Bays</div>
                    )}
                </div>
            ),
        },
        {
            name: 'shelfHeader',
            ignoreOnSubmit: true,
            inputElement: () => (
                <h5 style={{ textAlign: 'left', padding: 0, marginTop: '20px', fontWeight: 'bold' }}>Shelves (A-Z)</h5>
            ),
            display: (formData) =>
                zoneAisleBayTypes.includes(formData.type?.value) ||
                (replenTypes.includes(formData.type?.value) && replenishmentCreationMethod === 'ranges'),
        },
        {
            name: 'shelfStart',
            label: 'Start at Shelf',
            gridValue: 4,
            inputElement: 'textField',
            display: (formData) =>
                zoneAisleBayTypes.includes(formData.type?.value) ||
                (replenTypes.includes(formData.type?.value) && replenishmentCreationMethod === 'ranges'),
        },
        {
            name: 'shelfEnd',
            label: 'End at Shelf',
            gridValue: 4,
            inputElement: 'textField',
            display: (formData) =>
                zoneAisleBayTypes.includes(formData.type?.value) ||
                (replenTypes.includes(formData.type?.value) && replenishmentCreationMethod === 'ranges'),
        },
        {
            name: 'shelvesHint',
            ignoreOnSubmit: true,
            gridValue: 4,
            display: () => createLocProgress.hasOwnProperty('shelves') && !createLocProgress?.errors?.length,
            inputElement: () => (
                <div style={{ fontSize: '13px' }}>
                    <div>
                        {createLocProgress.shelves.range.length} shelves in range:{' '}
                        {createLocProgress.shelves.range.join(', ')}
                    </div>
                    {createLocProgress.shelves.new.length > 0 && (
                        <div>{createLocProgress.shelves.new.length} New Shelves</div>
                    )}
                    {createLocProgress.shelves.existing.length > 0 && (
                        <div>{createLocProgress.shelves.existing.length} Existing Shelves</div>
                    )}
                </div>
            ),
        },
        {
            name: 'postionHeader',
            ignoreOnSubmit: true,
            inputElement: () => (
                <h5 style={{ textAlign: 'left', padding: 0, margin: 0, fontWeight: 'bold' }}>Positions (Integers)</h5>
            ),
            display: (formData) => zoneAisleBayTypes.includes(formData.type?.value),
        },
        {
            name: 'positionStart',
            label: 'Start at Position',
            gridValue: 4,
            inputElement: 'textField',
            inputProps: { required: true, type: 'number', integer: true },
            display: (formData) => zoneAisleBayTypes.includes(formData.type?.value),
        },
        {
            name: 'positionEnd',
            label: 'End at Position',
            gridValue: 4,
            inputElement: 'textField',
            inputProps: { type: 'number', integer: true },
            display: (formData) => zoneAisleBayTypes.includes(formData.type?.value),
        },
        {
            name: 'positionsHint',
            ignoreOnSubmit: true,
            gridValue: 4,
            display: () => createLocProgress.hasOwnProperty('positions') && !createLocProgress?.errors?.length,
            inputElement: () => (
                <div style={{ fontSize: '13px' }}>
                    <div>
                        {createLocProgress.positions.range.length} positions in range:{' '}
                        {createLocProgress.positions.range.join(', ')}
                    </div>
                    {createLocProgress.positions.new.length > 0 && (
                        <div>{createLocProgress.positions.new.length} New Positions</div>
                    )}
                    {createLocProgress.positions.existing.length > 0 && (
                        <div style={{ color: 'red' }}>
                            {createLocProgress.positions.existing.length} Existing Positions
                        </div>
                    )}
                </div>
            ),
        },

        {
            name: 'lightHeader',
            ignoreOnSubmit: true,
            inputElement: () => (
                <h5 style={{ textAlign: 'left', padding: 0, marginTop: '20px', fontWeight: 'bold' }}>
                    Lights (Integers)
                </h5>
            ),
            display: (formData) =>
                replenTypes.includes(formData.type?.value) && replenishmentCreationMethod === 'ranges',
        },
        {
            name: 'lightStart',
            label: 'Start at Light',
            gridValue: 4,
            inputElement: 'textField',
            inputProps: { type: 'number', integer: true },
            display: (formData) =>
                replenTypes.includes(formData.type?.value) && replenishmentCreationMethod === 'ranges',
        },
        {
            name: 'lightEnd',
            label: 'End at Light',
            gridValue: 4,
            inputElement: 'textField',
            inputProps: { type: 'number', integer: true },
            display: (formData) =>
                replenTypes.includes(formData.type?.value) && replenishmentCreationMethod === 'ranges',
        },
        {
            name: 'childLineNumbersHint',
            ignoreOnSubmit: true,
            gridValue: 12,
            display: (formData) =>
                replenTypes.includes(formData.type?.value) && replenishmentCreationMethod === 'ranges',
            inputElement: () => (
                <div>
                    <h5 style={{ textAlign: 'left', padding: 0, marginTop: '20px', fontWeight: 'bold' }}>
                        Child Line Numbers
                    </h5>
                    <div style={{ fontSize: '13px', textAlign: 'left', fontStyle: 'italic' }}>
                        <div>
                            A comma-separated list of numbers (e.g., 1, 2, 5). These will be appended to each generated
                            parent location to create child locations.
                        </div>
                    </div>
                </div>
            ),
        },
        {
            name: 'childLineNumbers',
            label: 'Child Line Numbers',
            gridValue: 4,
            inputElement: 'textField',
            inputProps: { type: 'text' },
            display: (formData) =>
                replenTypes.includes(formData.type?.value) && replenishmentCreationMethod === 'ranges',
        },
        {
            name: 'childLineNumbersError',
            ignoreOnSubmit: true,
            gridValue: 12,
            display: () => !!childLineNumbersError,
            inputElement: () => <Alert severity="error">{childLineNumbersError}</Alert>,
        },

        {
            name: 'locationErrors',
            ignoreOnSubmit: true,
            gridValue: 12,
            display: () => createLocProgress?.errors?.length > 0,
            inputElement: () => (
                <div>
                    Errors found in provided ranges
                    {createLocProgress.errors.map((err) => (
                        <Alert severity="error">{err}</Alert>
                    ))}
                </div>
            ),
        },
    ];

    const formFields = [
        {
            name: 'type',
            inputElement: 'autocomplete',
            label: 'Type',
            gridValue: 12,
            inputProps: {
                required: true,
                opts: types
                    .filter((item) => {
                        if (v2Locations && item.creatableV2) {
                            return true;
                        }
                        if (!v2Locations && item.creatableV1) {
                            return true;
                        }
                        return false;
                    })
                    .map((item) => item.type),
            },
        },

        // fields that control the number of locations that will be created
        ...numberRangeFormFields,
        ...zoneAisleBayShelfPositionOrReplenFormFields,
        ...singleCreationFormFields,

        {
            name: 'name',
            inputElement: 'textField',
            label: 'Name',
            gridValue: 12,
            display: !!creatingType.type && creatingType.creatableV1 && creatingType.type !== 'TRANSFER',
            inputProps: { required: true },
        },

        {
            name: 'formDivider',
            ignoreOnSubmit: true,
            gridValue: 12,
            display: () =>
                !!creatingType.type &&
                creatingType.creatableV2 &&
                ![
                    'usable',
                    'tempZone',
                    'productionMethod',
                    'inventoryType',
                    'inventorySource',
                    'maxPallets',
                    'multipleLot',
                    'multipleSku',
                    'enableBulkMove',
                ].every((field) => Object.keys(creatingType.staticValues).includes(field)),
            inputElement: () => <hr style={{ margin: '30px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />,
        },
        // attributes that will apply to all bulk-created locations
        {
            // only display this if any of the attributes can be set
            name: 'attributeHint',
            ignoreOnSubmit: true,
            display: () =>
                !!creatingType.type &&
                creatingType.creatableV2 &&
                ![
                    'usable',
                    'tempZone',
                    'productionMethod',
                    'inventoryType',
                    'inventorySource',
                    'maxPallets',
                    'multipleLot',
                    'multipleSku',
                    'enableBulkMove',
                ].every((field) => Object.keys(creatingType.staticValues).includes(field)),
            inputElement: () => (
                <>
                    <h4 style={{ textAlign: 'center', fontWeight: 'bold' }}>Location Attributes</h4>
                    <h5 style={{ textAlign: 'center' }}>These will apply to all locations created</h5>
                </>
            ),
        },
        {
            name: 'usable',
            inputElement: 'select',
            label: 'Sellable',
            gridValue: 12,
            display: (formData) => displayFormField('usable', formData),
            required: true,
            inputProps: {
                required: true,
                opts: ['YES', 'NO'],
            },
        },
        {
            name: 'tempZone',
            inputElement: 'select',
            label: 'Temp Zone',
            gridValue: 12,
            display: (formData) => displayFormField('tempZone', formData),
            required: true,
            inputProps: {
                required: true,
                opts: tempZones,
            },
        },
        {
            name: 'productionMethod',
            inputElement: 'select',
            label: 'OG/CV',
            gridValue: 12,
            display: (formData) => displayFormField('productionMethod', formData),
            required: true,
            inputProps: {
                required: true,
                opts: [
                    { text: 'Both', value: 'BOTH' },
                    { text: 'Conventional', value: 'CONVENTIONAL' },
                    { text: 'Organic', value: 'ORGANIC' },
                ],
            },
        },
        {
            name: 'inventoryType',
            inputElement: 'select',
            label: 'Inventory Type',
            gridValue: 12,
            required: true,
            display: (formData) => displayFormField('inventoryType', formData),
            inputProps: {
                required: true,
                opts: ['Pallet', 'Case'],
            },
        },
        {
            name: 'inventorySource',
            inputElement: 'select',
            label: 'Inventory Source',
            gridValue: 12,
            required: true,
            display: (formData) => displayFormField('inventorySource', formData),
            inputProps: {
                required: true,
                opts: ['ANY', 'FIRST_PARTY', 'FBM'],
            },
        },
        {
            name: 'maxPallets',
            inputElement: 'textField',
            label: 'Max Pallets',
            gridValue: 12,
            required: true,
            display: (formData) => displayFormField('maxPallets', formData),
            inputProps: {
                type: 'number',
                integer: true,
            },
        },
        {
            name: 'multipleLot',
            inputElement: 'select',
            label: 'Multiple Lot',
            gridValue: 12,
            required: true,
            display: (formData) => displayFormField('multipleLot', formData),
            inputProps: {
                required: true,
                opts: ['YES', 'NO'],
            },
        },
        {
            name: 'multipleSku',
            inputElement: 'select',
            label: 'Multiple Sku',
            gridValue: 12,
            required: true,
            display: (formData) => displayFormField('multipleSku', formData),
            inputProps: {
                required: true,
                opts: ['YES', 'NO'],
            },
        },
        {
            name: 'enableBulkMove',
            inputElement: 'select',
            label: 'Enable Bulk Move',
            gridValue: 12,
            required: true,
            display: (formData) => canOffsite && displayFormField('enableBulkMove', formData),
            inputProps: {
                required: true,
                opts: ['YES', 'NO'],
            },
        },
    ];

    const confirmLocations = async (key) => {
        if (!key) {
            setNotification({ text: 'No key provided for location creation', level: 'error' });
            return;
        }

        const res = await api.confirmLocationCreation(key);
        if (!res.status) {
            setNotification({ text: `Failed to create locations ${res.msg}`, level: 'error' });
            return;
        }

        setLocationsCreatedResults(res.data.locations);
    };

    const getNewAndExistingLocations = async (formData) => {
        if (!formData.zone) {
            return;
        }
        const sendParams = {
            facilityID: user.getFacilityID(),
            zone: formData.zone,
            zoneType: formData.type?.value,
            aisleStart: formData.aisleStart,
            aisleEnd: formData.aisleEnd,
            bayStart: formData.bayStart,
            bayEnd: formData.bayEnd,
            shelfStart: formData.shelfStart,
            shelfEnd: formData.shelfEnd,
            positionStart: formData.positionStart,
            positionEnd: formData.positionEnd,
            tempZone: formData.tempZoneReserve,
        };

        const res = await api.getNewAndExistingLocations(sendParams);
        if (!res.status) {
            return;
        }

        setCreateLocProgress(res.data.results);
    };

    const createPrepLocationGroup = async (formData, resetForm) => {
        const res = await api.createPrepLocationGroup(formData);
        if (!res.status) {
            setNotification({ text: `Failed to create prep locations. ${res.msg}`, level: 'error' });
            return;
        }

        setNotification({ text: 'Prep locations created!', level: 'success' });
        resetForm();
    };

    const createSingleLocation = async (formData, resetForm) => {
        const res = await api.createSingleLocation(formData);
        if (!res.status) {
            setNotification({ text: `Failed to create location. ${res.msg}`, level: 'error' });
            return;
        }

        setNotification({ text: 'Location created!', level: 'success' });
        resetForm();
    };

    const getBulkLocationCreationPreview = async (formData) => {
        const res = await api.cacheLocationsToCreate(formData);
        if (!res.status || !res.data) {
            setNotification({ text: `Failed to create locations. ${res.msg}`, level: 'error' });
            return;
        }

        const { data } = res;

        const results = res.data.rows.map((row) => {
            if (res.data.existingLocations.includes(row.name)) {
                row.errors.push('Location already exists');
            }
            row.errors = row.errors.join(', ');
            return row;
        });
        data.results = results;

        const actions = [
            {
                name: 'Cancel',
                action: () => {
                    setToolbarActions([]);
                    setCreateResults(null);
                },
            },
        ];

        if (!data.error && data.results.length > 0) {
            actions.push({
                name: 'Confirm Locations',
                action: async () => {
                    const { key } = data;
                    await confirmLocations(key);
                },
            });
        }

        if (!data.results.length && !data.error) {
            data.errors.push('No new locations to create in given ranges');
        }

        setCreateResults(data);
        setToolbarActions(actions);
    };

    const create = async (formData, resetForm) => {
        const sendParams = {
            name: formData.name,
            type: formData.type,
            facilityID: user.getFacilityID(),
            usable: formData.usable === 'YES',
            tempZone: formData.type === 'RESERVE' ? formData.tempZoneReserve : formData.tempZone,
            productionMethod: formData.productionMethod,
            inventoryType: formData.inventoryType,
            inventorySource: formData.inventorySource,
            maxPallets: formData.inventoryType === 'Case' ? 0 : formData.maxPallets,
            multipleLot: formData.multipleLot === 'YES',
            multipleSku: formData.multipleLot === 'NO' ? false : formData.multipleSku === 'YES',
            enableBulkMove: formData.enableBulkMove === 'YES',
        };

        if (formData.type === 'PREP') {
            sendParams.groupName = `Prep ${formData.suffix}`;
        }

        if (startsAtEndsAtTypes.includes(formData.type)) {
            sendParams.startAt = formData.startAt;
            sendParams.endAt = formData.endAt;
        }

        if (zoneAisleBayTypes.includes(formData.type)) {
            sendParams.zone = formData.zone;
            sendParams.aisleStart = formData.aisleStart;
            sendParams.aisleEnd = formData.aisleEnd;
            sendParams.bayStart = formData.bayStart;
            sendParams.bayEnd = formData.bayEnd;
            sendParams.shelfStart = formData.shelfStart;
            sendParams.shelfEnd = formData.shelfEnd;
            sendParams.positionStart = formData.positionStart;
            sendParams.positionEnd = formData.positionEnd;
        }

        if (replenTypes.includes(formData.type)) {
            sendParams.replenishmentCreationMethod = replenishmentCreationMethod;
            if (replenishmentCreationMethod === 'csv') {
                sendParams.replenishmentFile = formData.replenishmentLocationFile;
            } else {
                sendParams.lineStart = formData.lineStart;
                sendParams.lineEnd = formData.lineEnd;
                sendParams.bayStart = formData.bayStart;
                sendParams.bayEnd = formData.bayEnd;
                sendParams.shelfStart = formData.shelfStart;
                sendParams.shelfEnd = formData.shelfEnd;
                sendParams.lightStart = formData.lightStart;
                sendParams.lightEnd = formData.lightEnd;
                let childLineNumbers = (formData.childLineNumbers || '').trim();
                if (childLineNumbers.endsWith(',')) {
                    childLineNumbers = childLineNumbers.slice(0, -1);
                }
                sendParams.childLineNumbers = childLineNumbers;
            }
        }

        if (singleCreationTypes.includes(formData.type)) {
            sendParams.suffix = formData.suffix;
        }

        if (formData.type === 'PREP') {
            createPrepLocationGroup(sendParams, resetForm);
        } else if (singleCreationTypes.includes(formData.type)) {
            createSingleLocation(sendParams, resetForm);
        } else {
            getBulkLocationCreationPreview(sendParams);
        }
    };

    const getTempZones = async () => {
        const res = await api.getLocationTempZones();
        if (!res.status) {
            setNotification({ text: 'Failed to get location temp zones', level: 'error' });
            return;
        }

        setTempZones(res.data.tempZones);
    };

    const getTypes = async () => {
        const res = await api.getLocationTypesV2(user.getFacilityID());
        if (!res.status) {
            setNotification({ text: 'Failed to get location types', level: 'error' });
            return;
        }

        setTypes(res.data.types);
    };

    const resetState = () => {
        setLocationsCreatedResults(null);
        setCreateResults(null);
        setToolbarActions([]);
        setCreatingType({});
        setCreateLocProgress({});
        setViewNewAndExistingLocationsInZone(false);
        setChildLineNumbersError(null);
    };

    useEffect(() => {
        getTempZones();
        getTypes();
    }, []);

    return (
        <div>
            <h3>
                Create Locations
                {!!createResults && !locationsCreatedResults && ` - Review`}
            </h3>
            <Notifications options={notification} />
            {viewNewAndExistingLocationsInZone && (
                <ViewLocationsInZone
                    creatingTypePrefix={reservePrefix}
                    createLocProgress={createLocProgress}
                    facilityID={user.getFacilityID()}
                    zone={zone}
                    zoneType={creatingType.type}
                />
            )}
            {!locationsCreatedResults && (
                <div style={{ outline: 0, backgroundColor: 'white', display: createResults ? 'none' : undefined }}>
                    <FormComponent
                        compact
                        formFields={formFields}
                        onSubmit={async (formData, resetForm) => {
                            create(formData, resetForm);
                        }}
                        button={{
                            disabled: () => createLocProgress?.errors?.length > 0 || !!childLineNumbersError,
                        }}
                        onFormDataChange={(formData) => {
                            const type = formData.type?.value;
                            if (type) {
                                setCreatingType(types.find((item) => item.type === type));
                                setResultsColumnsByType(type);
                            } else {
                                setCreatingType({});
                            }

                            if (type === 'RESERVE') {
                                getNewAndExistingLocations(formData);
                                setZone(formData.zone);
                            }

                            const regex = /^\s*(\d+(\s*,\s*\d+)*,?)?\s*$/;
                            if (!regex.test(formData.childLineNumbers || '')) {
                                setChildLineNumbersError(
                                    'Please enter a valid comma-separated list of numbers (e.g., 1, 2, 5).',
                                );
                            } else {
                                setChildLineNumbersError(null);
                            }
                        }}
                    />
                </div>
            )}
            {!!locationsCreatedResults && (
                <>
                    <Alert severity="success">Locations Created!</Alert>
                    <DataTable
                        columns={columns}
                        toolbarActions={[
                            {
                                name: 'Download All Labels',
                                action: () => {
                                    const ids = locationsCreatedResults.map((item) => item.id);
                                    api.bulkDownloadLocationLabels(ids);
                                },
                            },
                            {
                                name: 'Done',
                                action: () => {
                                    resetState();
                                },
                            },
                        ]}
                        data={locationsCreatedResults}
                    />
                </>
            )}
            {!!createResults && !locationsCreatedResults && (
                <>
                    {createResults.error ? (
                        <>
                            {createResults.errors.map((item, ind) => (
                                <Alert key={`${ind}`} severity="error">
                                    {item}
                                </Alert>
                            ))}
                            {createResults.existingLocations.length > 0 && (
                                <Alert severity="error">
                                    Locations already exist: {createResults.existingLocations.join(', ')}
                                </Alert>
                            )}
                        </>
                    ) : (
                        <Alert severity="success">
                            Confirming will create {createResults.results.length} new {creatingType.type} locations.
                        </Alert>
                    )}
                    {createResults.warnings.length > 0 && (
                        <div style={{ maxHeight: '400px', overflowY: 'scroll' }}>
                            <div>{createResults.warnings.length} Warnings</div>
                            {createResults.warnings.map((item, ind) => (
                                <Alert key={`${ind}`} severity="warning">
                                    {item}
                                </Alert>
                            ))}
                        </div>
                    )}
                    <DataTable
                        columns={columns}
                        toolbarActions={toolbarActions}
                        data={createResults.results}
                        rowStyle={(row) => {
                            if (row.errors.length) {
                                return { color: 'red', border: '3px solid red' };
                            }
                            return {};
                        }}
                    />
                </>
            )}
        </div>
    );
}

export { BulkCreateLocations };
